import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { restaurantKeys, listsKeys } from '@/api/keys';
import {
  RestaurantRatingsResponse,
  RestaurantNotesResponse,
} from '@/types/server/restaurantApiResponses';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

// Menu item image types
export interface MenuItemImageResponse {
  id: number;
  menu_item_id: number;
  url: string;
  user: {
    id: number;
    firstName: string;
    lastName: string;
  };
  created_at: string;
}

export interface UploadMenuItemImageParams {
  restaurantId: number;
  menuItemId: number;
  publicId: string;
  /**
   * Skip this call's own cache invalidation. Used by the rating flow's
   * batched photo fan-out (`uploadPendingMenuItemImages` in
   * useRestaurantRating.ts), which invalidates once after the whole
   * Promise.allSettled batch settles instead of once per image. Defaults to
   * false/undefined so existing single-image callers (e.g.
   * MenuItemDetailModal) keep invalidating immediately on each upload,
   * unchanged.
   */
  skipInvalidation?: boolean;
}

export interface DiscardUploadsParams {
  publicIds: string[];
  resourceType?: 'image' | 'video';
}

export interface DeleteMenuItemImageParams {
  restaurantId: number;
  menuItemId: number;
  imageId: number;
}

// Types
export interface SubmitRestaurantRatingsParams {
  restaurantId: number;
  ratings: RestaurantRatingsResponse;
}

export interface SubmitRestaurantNotesParams {
  restaurantId: number;
  notes: string;
}

/**
 * Hook to submit restaurant ratings
 * On success: invalidates related queries to refetch fresh data
 */
export function useSubmitRestaurantRating() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ restaurantId, ratings }: SubmitRestaurantRatingsParams) =>
      apiFetch<RestaurantRatingsResponse>(`/restaurants/${restaurantId}/ratings/`, {
        method: 'POST',
        body: ratings,
      }),
    onSuccess: (data, variables) => {
      // Track restaurant rating (PostHog - behavioral analytics)
      analytics.capture(AnalyticsEvents.RESTAURANT_RATED, {
        restaurant_id: variables.restaurantId,
        ratings: variables.ratings,
      });

      // Note: AppsFlyer first_restaurant_rating conversion is tracked server-side
      // when we can confirm it's the user's first rating

      // Invalidate search results to refetch with updated rating
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.searches(),
      });

      // Invalidate the specific restaurant's ratings query
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.ratings(variables.restaurantId),
      });

      // Invalidate menu items so the newly-rated item reflects its rating immediately
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.menuItems(variables.restaurantId),
      });

      // Invalidate restaurant details
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.detail(variables.restaurantId),
      });
    },
  });
}

/**
 * Hook to submit restaurant notes
 */
export function useSubmitRestaurantNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ restaurantId, notes }: SubmitRestaurantNotesParams) =>
      apiFetch<RestaurantNotesResponse>(`/restaurants/${restaurantId}/notes/`, {
        method: 'POST',
        body: { notes },
      }),
    onSuccess: (_, variables) => {
      // Track notes saved
      analytics.capture(AnalyticsEvents.RESTAURANT_NOTES_SAVED, {
        restaurant_id: variables.restaurantId,
        note_length: variables.notes.length,
      });

      // Invalidate restaurant details to show updated notes
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.detail(variables.restaurantId),
      });
      // Invalidate all list details since the restaurant could be in multiple lists
      queryClient.invalidateQueries({
        queryKey: listsKeys.details(),
      });
    },
  });
}

/**
 * Hook to upload a menu item image
 * Each user can upload up to 3 images per menu item
 */
export function useUploadMenuItemImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ restaurantId, menuItemId, publicId }: UploadMenuItemImageParams) =>
      apiFetch<MenuItemImageResponse>(
        `/restaurants/${restaurantId}/menu-items/${menuItemId}/images/`,
        {
          method: 'POST',
          body: { public_id: publicId },
        }
      ),
    onSuccess: (_, variables) => {
      if (variables.skipInvalidation) return;

      // Invalidate menu items query to refetch with new image
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.menuItems(variables.restaurantId),
      });

      // Invalidate restaurant details
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.detail(variables.restaurantId),
      });
    },
  });
}

/**
 * Batch-discards pending Cloudinary uploads that were never linked to a menu
 * item — abandoned photo picks, a removed thumbnail, or a removed dish.
 * Fire-and-forget by design: the endpoint always returns 202 and performs
 * ownership/reference checks asynchronously server-side, so there is nothing
 * meaningful to await here. Chunks any list larger than 10 ids (the
 * endpoint's per-request cap) into multiple requests.
 */
const DISCARD_UPLOADS_CHUNK_SIZE = 10;

export function useDiscardUploads() {
  const { mutate } = useMutation({
    mutationFn: ({ publicIds, resourceType = 'image' }: DiscardUploadsParams) =>
      apiFetch<void>('/uploads/discard/', {
        method: 'POST',
        body: { publicIds, resourceType },
      }),
  });

  const discardUploads = useCallback(
    (publicIds: string[], resourceType: 'image' | 'video' = 'image') => {
      for (let i = 0; i < publicIds.length; i += DISCARD_UPLOADS_CHUNK_SIZE) {
        mutate({ publicIds: publicIds.slice(i, i + DISCARD_UPLOADS_CHUNK_SIZE), resourceType });
      }
    },
    [mutate]
  );

  return { discardUploads };
}

/**
 * Hook to delete a menu item image
 * Users can only delete their own images
 */
export function useDeleteMenuItemImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ restaurantId, menuItemId, imageId }: DeleteMenuItemImageParams) =>
      apiFetch<void>(
        `/restaurants/${restaurantId}/menu-items/${menuItemId}/images/${imageId}/`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: (_, variables) => {
      // Invalidate menu items query to refetch without deleted image
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.menuItems(variables.restaurantId),
      });

      // Invalidate restaurant details
      queryClient.invalidateQueries({
        queryKey: restaurantKeys.detail(variables.restaurantId),
      });
    },
  });
}
